/**
 * ConfirmFundingForm Component
 * Form to confirm a pending funding instruction by entering a SPEI bank reference.
 * Uses the confirm mutation from useFunding.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useAuth, useToast } from '@/hooks';
import { confirmFunding } from '@/services/credit-payment.service';

// ─── Props ───────────────────────────────────────────────────────────────────

interface ConfirmFundingFormProps {
  fundingId: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ConfirmFundingForm({ fundingId }: ConfirmFundingFormProps) {
  const [bankReference, setBankReference] = useState('');
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const confirmMutation = useMutation({
    mutationFn: () =>
      confirmFunding(fundingId, {
        bankReference,
        confirmedBy: user?.email ?? 'unknown',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['funding'] });
      toast.success('Transferencia confirmada exitosamente');
      setBankReference('');
    },
    onError: () => {
      toast.error('Error al confirmar la transferencia');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!bankReference.trim()) {
      toast.warning('Ingresa la referencia bancaria SPEI');
      return;
    }
    confirmMutation.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Referencia bancaria SPEI"
        placeholder="Ej: SPEI-2026-0303-001"
        value={bankReference}
        onChange={(e) => setBankReference(e.target.value)}
        required
        hint="Ingresa la referencia de la transferencia SPEI realizada"
      />
      <Button
        type="submit"
        isLoading={confirmMutation.isPending}
        disabled={!bankReference.trim()}
      >
        Confirmar Transferencia
      </Button>
    </form>
  );
}
